import type { TScreenOrientation, TScreenRotation } from '@lucimark/shared/models/screen-rotation'
import Emittery from 'emittery'

export type { TScreenOrientation, TScreenRotation }
export interface TScreenSize { width: number, height: number }

export enum ScreenEvent {
  ROTATION = 'rotation',
  ORIENTATION = 'orientation',
  SIZE = 'size'
}

export interface ScreenEventMap {
  [ScreenEvent.ROTATION]: TScreenRotation
  [ScreenEvent.ORIENTATION]: TScreenOrientation
  [ScreenEvent.SIZE]: TScreenSize

}

/** Persisted for boot splash (index.html inline script); Android copies via persistScreenRotation. */
export const SCREEN_ROTATION_STORAGE_KEY = 'LUCIMARK:SCREEN_ROTATION'

const SCREEN_ROTATION_PREFIX = SCREEN_ROTATION_STORAGE_KEY

export interface IScreenService {
  size: TScreenSize
  orientation: TScreenOrientation
  rotation: TScreenRotation
  rotateScreenLeft: () => void
  rotateScreenRight: () => void
}

export class ScreenService extends Emittery<ScreenEventMap> implements IScreenService {
  private _rotation: TScreenRotation = '0'
  private _orientation: TScreenOrientation = 'horizontal'
  private _size: TScreenSize = { width: 0, height: 0 }
  readonly horizontalAngles = ['0', '180']

  constructor() {
    super()
    this._rotation = (window.localStorage.getItem(SCREEN_ROTATION_PREFIX) ?? '0') as TScreenRotation
    this._orientation = this.horizontalAngles.includes(this._rotation) ? 'horizontal' : 'vertical'
    window.addEventListener('resize', this._setScreenSize.bind(this))
    this._setScreenSize()
  }

  private _setScreenSize() {
    const { innerHeight: height, innerWidth: width } = window
    this._size = { height, width }
    void this.emit(ScreenEvent.SIZE, this._size)
  }

  get size() {
    return this._size
  }

  get orientation() {
    return this._orientation
  }

  get rotation() {
    return this._rotation
  }

  set rotation(rotation: TScreenRotation) {
    window.localStorage.setItem(SCREEN_ROTATION_PREFIX, rotation)
    this._rotation = rotation
    this._orientation = this.horizontalAngles.includes(this._rotation) ? 'horizontal' : 'vertical'

    void this.emit(ScreenEvent.ROTATION, this._rotation)
    void this.emit(ScreenEvent.ORIENTATION, this._orientation)
  }

  rotateScreenLeft() {
    let newRotation: TScreenRotation = '0'
    switch (this.rotation) {
      case '0':
        newRotation = '270'
        break
      case '90':
        newRotation = '0'
        break
      case '180':
        newRotation = '90'
        break
      case '270':
        newRotation = '180'
        break
    }
    this.rotation = newRotation
  }

  rotateScreenRight() {
    let newRotation: TScreenRotation = '0'
    switch (this.rotation) {
      case '0':
        newRotation = '90'
        break
      case '90':
        newRotation = '180'
        break
      case '180':
        newRotation = '270'
        break
      case '270':
        newRotation = '0'
        break
    }
    this.rotation = newRotation
  }
}

export type TScreenService = ScreenService
